import React, { useContext, useLayoutEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GenericFormPage, FormDataContext, type FormDataContextValue } from '../../components/generic';
import { useAuth } from '../../contexts/AuthContext';
import { useAppointment, Appointment, useAppStatusesForSelect, useDurationsForSelect, sortDurationsByOrder } from './useData';
import {
  moduleConfig,
  getInitialFormData,
  mapItemToFormData,
} from './config';

type FormAppointment = Omit<Appointment, 'clientId' | 'assignedTo' | 'clientAppDuration' | 'clientAppStatus' | 'inviteEmails'> & {
  clientId?: string | number;
  assignedTo?: string | number;
  clientAppDuration?: string | number;
  clientAppStatus?: string | number;
  inviteEmails?: string | string[];
};

const parseInviteEmails = (raw?: string | string[]): string[] | undefined => {
  if (raw == null) return undefined;
  if (Array.isArray(raw)) {
    const emails = raw.map(String).map((e) => e.trim()).filter(Boolean);
    return emails.length > 0 ? emails : undefined;
  }
  const emails = raw.split(/[\n,;]+/).map((e) => e.trim()).filter(Boolean);
  return emails.length > 0 ? emails : undefined;
};

const convertToNumber = (value: string | number | undefined): number | undefined => {
  if (value === undefined || value === null || String(value) === '') {
    return undefined;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

interface AppointmentFormDefaultsProps {
  userId?: number;
  defaultStatus: string;
  defaultDurationId: string;
}

/** Apply assignee / status / duration once async options and auth are ready. */
const AppointmentFormDefaults: React.FC<AppointmentFormDefaultsProps> = ({
  userId,
  defaultStatus,
  defaultDurationId,
}) => {
  const ctx = useContext(FormDataContext) as FormDataContextValue | null;
  useLayoutEffect(() => {
    if (!ctx) return;
    const updates: Record<string, string> = {};
    const current = ctx.formDataRef.current;
    if (userId != null && (current.assignedTo === '' || current.assignedTo == null)) {
      updates.assignedTo = String(userId);
    }
    if (defaultStatus && (current.clientAppStatus === '' || current.clientAppStatus == null)) {
      updates.clientAppStatus = defaultStatus;
    }
    if (defaultDurationId && (current.clientAppDuration === '' || current.clientAppDuration == null)) {
      updates.clientAppDuration = defaultDurationId;
    }
    if (Object.keys(updates).length > 0) {
      const current = ctx.formDataRef.current;
      const hasChange = Object.entries(updates).some(
        ([key, value]) => current[key] !== value,
      );
      if (hasChange) {
        ctx.setMultipleFieldValues(updates);
      }
    }
  }, [ctx, userId, defaultStatus, defaultDurationId]);
  return null;
};

const Form: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const scheduleDateTimeFromUrl = searchParams.get('clientAppScheduleDateTime');
  const { options: appStatusOptions } = useAppStatusesForSelect();
  const { durations } = useDurationsForSelect();

  const defaultDurationId = useMemo(() => {
    const thirty = durations.find((d) => d.pd_min === 30);
    if (thirty?.id != null) return String(thirty.id);
    const first = durations[0];
    return first?.id != null ? String(first.id) : '';
  }, [durations]);

  const defaultStatus = useMemo(
    () => (appStatusOptions.length > 0 ? String(appStatusOptions[0].value) : ''),
    [appStatusOptions],
  );

  const configWithOptions = useMemo(() => {
    const fields = moduleConfig.fields.map((field) => {
      if (field.name === 'clientId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Client',
          placeholder: 'Select a client (optional)',
          options: [{ value: '', label: 'None' }],
          apiEndpoint: '/crm-clients',
          labelField: 'clientName',
          valueField: 'clientId' as const,
        };
      }
      if (field.name === 'assignedTo') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Assigned to',
          placeholder: 'Who is responsible?',
          options: [],
          apiEndpoint: '/users',
          labelField: 'userDisplayName',
          valueField: 'id' as const,
        };
      }
      if (field.name === 'clientAppDuration') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Duration',
          placeholder: 'How long?',
          options: [{ value: '', label: 'None' }],
          apiEndpoint: '/p-crm-duration',
          labelField: 'pd_descr',
          valueField: 'id' as const,
          sortApiList: sortDurationsByOrder,
        };
      }
      if (field.name === 'clientAppStatus') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Status',
          placeholder: 'Select status',
          options: appStatusOptions,
        };
      }
      return field;
    });

    const customMapItemToFormData = (item: Appointment): Partial<FormAppointment> => {
      const baseData = mapItemToFormData(item);
      return {
        ...baseData,
        clientId: baseData.clientId ? String(baseData.clientId) : '',
        assignedTo: baseData.assignedTo ? String(baseData.assignedTo) : '',
        clientAppDuration: baseData.clientAppDuration ? String(baseData.clientAppDuration) : '',
        clientAppStatus: baseData.clientAppStatus ? String(baseData.clientAppStatus) : '',
        inviteEmails: Array.isArray(item.inviteEmails) ? item.inviteEmails.join('\n') : '',
      };
    };

    const customGetInitialFormData = (): Partial<FormAppointment> => ({
      ...getInitialFormData(),
      clientId: '',
      assignedTo: user?.id != null ? String(user.id) : '',
      clientAppDuration: defaultDurationId,
      clientAppStatus: defaultStatus,
      clientAppScheduleDateTime: scheduleDateTimeFromUrl || undefined,
      inviteEmails: '',
    });

    const customCreate = async (data: Partial<FormAppointment>) => {
      const assignedTo =
        convertToNumber(data.assignedTo) ?? user?.id;
      const clientAppStatus =
        convertToNumber(data.clientAppStatus) ?? convertToNumber(defaultStatus);
      const clientAppDuration =
        convertToNumber(data.clientAppDuration) ?? convertToNumber(defaultDurationId);

      const processedData: Partial<Appointment> = {
        clientAppName: String(data.clientAppName ?? '').trim(),
        clientAppScheduleDateTime: data.clientAppScheduleDateTime || undefined,
        clientAppNotes: data.clientAppNotes || undefined,
        clientId: convertToNumber(data.clientId),
        assignedTo,
        clientAppDuration,
        clientAppStatus,
        inviteEmails: parseInviteEmails(data.inviteEmails),
      };
      return await moduleConfig.form.create(processedData);
    };

    const customUpdate = async (id: number, data: Partial<FormAppointment>) => {
      const processedData: Partial<Appointment> = {
        clientAppName: String(data.clientAppName ?? '').trim(),
        clientAppScheduleDateTime: data.clientAppScheduleDateTime || undefined,
        clientAppNotes: data.clientAppNotes || undefined,
        clientId: convertToNumber(data.clientId),
        assignedTo: convertToNumber(data.assignedTo),
        clientAppDuration: convertToNumber(data.clientAppDuration),
        clientAppStatus: convertToNumber(data.clientAppStatus),
        inviteEmails: parseInviteEmails(data.inviteEmails),
      };
      return await moduleConfig.form.update(id, processedData);
    };

    return {
      ...moduleConfig,
      fields,
      getInitialFormData: customGetInitialFormData as () => Partial<Appointment>,
      mapItemToFormData: customMapItemToFormData as (item: Appointment) => Partial<Appointment>,
      form: {
        ...moduleConfig.form,
        create: customCreate as (data: Partial<Appointment>) => Promise<Appointment>,
        update: customUpdate as (id: number, data: Partial<Appointment>) => Promise<Appointment>,
        bootstrap: (
          <AppointmentFormDefaults
            userId={user?.id}
            defaultStatus={defaultStatus}
            defaultDurationId={defaultDurationId}
          />
        ),
      },
    };
  }, [
    appStatusOptions,
    defaultDurationId,
    defaultStatus,
    scheduleDateTimeFromUrl,
    user?.id,
  ]);

  return (
    <GenericFormPage<Appointment>
      useData={useAppointment}
      config={configWithOptions}
    />
  );
};

export default Form;
